import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  MenuItem,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { apiClient } from 'shared/api';
import {
  getLeadStatusClassName,
  getLeadStatusLabel,
  LEAD_STATUSES,
  LeadStatus,
  normalizeLeadStatus,
} from 'shared/model/constants/lead-status';
import { ROUTE_PATHS } from 'shared/model/constants/paths';
import { ButtonCustom } from 'shared/ui/button-custom';
import { DashboardHeaderBackLink } from 'widgets/dashboard-header-back-link';
import styles from './leads-admin.module.scss';

type LeadType = 'selection' | 'insurance' | 'leasing';

interface Lead {
  id: number;
  documentId: string;
  equipment?: string;
  condition?: string;
  objectType?: string;
  region?: string;
  amount?: string;
  deadline?: string;
  name?: string;
  phone?: string;
  comment?: string;
  type?: LeadType;
  template?: string;
  path?: string;
  status?: LeadStatus | string;
  createdAt: string;
  updatedAt: string;
}

interface SearchFilters {
  name: string;
  phone: string;
  region: string;
  details: string;
}

interface TabCounts {
  selection: number;
  insurance: number;
  leasing: number;
}

const TAB_TYPES: LeadType[] = ['selection', 'insurance', 'leasing'];

const INITIAL_FILTERS: SearchFilters = {
  name: '',
  phone: '',
  region: '',
  details: '',
};

const INITIAL_COUNTS: TabCounts = {
  selection: 0,
  insurance: 0,
  leasing: 0,
};

function a11yProps(index: number) {
  return {
    id: `lead-tab-${index}`,
    'aria-controls': `lead-tabpanel-${index}`,
  };
}

const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'white',
    fontSize: '14px',
  },
  '& .MuiInputBase-input': {
    padding: '8px 12px',
  },
};

const buildStrapiParams = (type: LeadType, filters: SearchFilters, withCount = false) => {
  const params: Record<string, string | number | boolean> = {
    sort: 'updatedAt:desc',
    'filters[type][$eq]': type,
  };

  if (withCount) {
    params['pagination[pageSize]'] = 1;
    params['pagination[withCount]'] = true;
  }

  if (filters.name) {
    params['filters[name][$containsi]'] = filters.name;
  }

  if (filters.phone) {
    params['filters[phone][$containsi]'] = filters.phone;
  }

  if (filters.region) {
    params['filters[region][$containsi]'] = filters.region;
  }

  if (filters.details) {
    const detailsField =
      type === 'selection' ? 'equipment' : type === 'insurance' ? 'objectType' : 'comment';
    params[`filters[${detailsField}][$containsi]`] = filters.details;
  }

  return params;
};

const hasActiveFilters = (filters: SearchFilters) =>
  Object.values(filters).some((value) => value.trim().length > 0);

export const LeadsAdmin = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [tabCounts, setTabCounts] = useState<TabCounts>(INITIAL_COUNTS);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [searchFilters, setSearchFilters] = useState<SearchFilters>(INITIAL_FILTERS);

  const activeType = TAB_TYPES[tabValue];

  const fetchTabCounts = useCallback(async () => {
    try {
      const responses = await Promise.all(
        TAB_TYPES.map((type) =>
          apiClient.get('/leads', {
            params: buildStrapiParams(type, INITIAL_FILTERS, true),
          }),
        ),
      );

      setTabCounts({
        selection: responses[0].data.meta?.pagination?.total ?? 0,
        insurance: responses[1].data.meta?.pagination?.total ?? 0,
        leasing: responses[2].data.meta?.pagination?.total ?? 0,
      });
    } catch (error) {
      console.error('Ошибка при загрузке счётчиков заявок:', error);
    }
  }, []);

  const fetchLeads = useCallback(async (type: LeadType, filters: SearchFilters) => {
    try {
      setLoading(true);
      const response = await apiClient.get('/leads', {
        params: buildStrapiParams(type, filters),
      });
      setLeads(response.data.data || []);
    } catch (error) {
      console.error('Ошибка при загрузке заявок:', error);
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTabCounts();
  }, [fetchTabCounts]);

  useEffect(() => {
    setSelectedIds(new Set());
    fetchLeads(activeType, searchFilters);
  }, [activeType, searchFilters, fetchLeads]);

  const handleFilterChange = (field: keyof SearchFilters, value: string) => {
    setSearchFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(leads.map((lead) => lead.id)));
      return;
    }

    setSelectedIds(new Set());
  };

  const handleSelectOne = (id: number, checked: boolean) => {
    const newSelected = new Set(selectedIds);

    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }

    setSelectedIds(newSelected);
  };

  const isAllSelected = leads.length > 0 && leads.every((lead) => selectedIds.has(lead.id));
  const isIndeterminate = selectedIds.size > 0 && !isAllSelected;

  const refreshData = async () => {
    await Promise.all([fetchTabCounts(), fetchLeads(activeType, searchFilters)]);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ru-RU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleViewLead = (lead: Lead) => {
    navigate(`${ROUTE_PATHS.ADMIN_LEADS}/${lead.documentId || lead.id}`);
  };

  const handleDeleteLead = async (lead: Lead) => {
    const prompt = await confirm('Вы уверены, что хотите удалить эту заявку?');
    if (!prompt) return;

    try {
      await apiClient.delete(`/leads/${lead.documentId || lead.id}`);
      await refreshData();
    } catch (error) {
      console.error('Ошибка при удалении заявки:', error);
    }
  };

  const handleStatusChange = async (lead: Lead, status: LeadStatus) => {
    try {
      await apiClient.put(`/leads/${lead.documentId || lead.id}`, {
        data: { status },
      });

      setLeads((prev) =>
        prev.map((item) => (item.id === lead.id ? { ...item, status } : item)),
      );
    } catch (error) {
      console.error('Ошибка при обновлении статуса:', error);
    }
  };

  const handleBulkDelete = () => {
    if (selectedIds.size > 0) {
      setShowDeleteDialog(true);
    }
  };

  const performBulkDelete = async () => {
    try {
      const deletePromises = Array.from(selectedIds).map((id) => {
        const lead = leads.find((item) => item.id === id);
        if (!lead) return Promise.resolve();

        return apiClient.delete(`/leads/${lead.documentId || lead.id}`);
      });

      await Promise.all(deletePromises);
      setSelectedIds(new Set());
      setShowDeleteDialog(false);
      await refreshData();
    } catch (error) {
      console.error('Ошибка при удалении заявок:', error);
    }
  };

  const getDetailsValue = (lead: Lead, type: LeadType) => {
    if (type === 'insurance') return lead.objectType || '-';
    if (type === 'leasing') return lead.comment || '-';
    return lead.equipment || '-';
  };

  const renderEmptyState = (type: LeadType) => (
    <Box className={styles.emptyState}>
      <Typography variant='h6' color='text.secondary' align='center'>
        Нет заявок с лендингов
      </Typography>
      <Typography variant='body2' color='text.secondary' align='center'>
        {type === 'selection' && 'Заявки на подбор будут отображаться здесь'}
        {type === 'insurance' && 'Заявки на страхование будут отображаться здесь'}
        {type === 'leasing' && 'Заявки на лизинг будут отображаться здесь'}
      </Typography>
    </Box>
  );

  const renderTable = (type: LeadType) => {
    if (loading) {
      return (
        <Box className={styles.loading}>
          <Typography>Загрузка...</Typography>
        </Box>
      );
    }

    if (leads.length === 0 && !hasActiveFilters(searchFilters)) {
      return renderEmptyState(type);
    }

    return (
      <Box>
        <Box
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
          }}
        >
          <Box style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {selectedIds.size > 0 && (
              <>
                <Typography variant='body2' color='text.secondary'>
                  Выбрано: {selectedIds.size}
                </Typography>
                <ButtonCustom onClick={handleBulkDelete} className={styles.redBtn}>
                  Удалить выбранные
                </ButtonCustom>
              </>
            )}
          </Box>
        </Box>

        <TableContainer component={Box} className={styles.tableContainer}>
          <Table className={styles.table} sx={{ minWidth: 1200 }} aria-label='Список заявок'>
            <TableHead className={styles.tableHead}>
              <TableRow className={styles.tableHeadRow}>
                <TableCell
                  className={`${styles.tableHeadCellFirst} ${styles.tableHeadCell}`}
                  style={{ width: '60px' }}
                />
                <TableCell className={styles.tableHeadCell}>
                  <TextField
                    variant='outlined'
                    size='small'
                    placeholder='Имя'
                    value={searchFilters.name}
                    onChange={(e) => handleFilterChange('name', e.target.value)}
                    sx={textFieldSx}
                  />
                </TableCell>
                <TableCell className={`${styles.tableHeadCell} ${styles.tableCellMobile}`}>
                  <TextField
                    variant='outlined'
                    size='small'
                    placeholder='Телефон'
                    value={searchFilters.phone}
                    onChange={(e) => handleFilterChange('phone', e.target.value)}
                    sx={textFieldSx}
                  />
                </TableCell>

                <TableCell className={styles.tableHeadCell} align='center'>
                  Обновлено
                </TableCell>
                <TableCell className={styles.tableHeadCell} align='center'>
                  Статус
                </TableCell>
                <TableCell className={styles.tableHeadCell} align='center'>
                  Действия
                </TableCell>
              </TableRow>
              <TableRow className={styles.tableHeadRow}>
                <TableCell
                  className={`${styles.tableHeadCellFirst} ${styles.tableHeadCell}`}
                  style={{ width: '60px' }}
                >
                  <Checkbox
                    checked={isAllSelected}
                    indeterminate={isIndeterminate}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    color='primary'
                  />
                </TableCell>
                <TableCell className={styles.tableHeadCell}>Имя</TableCell>
                <TableCell className={`${styles.tableHeadCell} ${styles.tableCellMobile}`}>
                  Телефон
                </TableCell>
                <TableCell className={styles.tableHeadCell} align='center'>
                  Дата
                </TableCell>
                <TableCell className={styles.tableHeadCell} align='center'>
                  -
                </TableCell>
                <TableCell className={styles.tableHeadCell} align='center'>
                  -
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody className={styles.tableBody}>
              {leads.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align='center' style={{ padding: '40px 20px' }}>
                    <Typography variant='h6' color='text.secondary'>
                      По вашему запросу ничего не найдено
                    </Typography>
                    <Typography variant='body2' color='text.secondary' style={{ marginTop: '8px' }}>
                      Попробуйте изменить критерии поиска
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                leads.map((lead) => (
                  <TableRow
                    key={lead.id}
                    className={styles.tableBodyRow}
                    sx={{
                      '& td, & th': { border: 0 },
                      marginTop: '10px',
                    }}
                  >
                    <TableCell
                      className={`${styles.tableBodyCellFirst} ${styles.tableBodyCell}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        checked={selectedIds.has(lead.id)}
                        onChange={(e) => handleSelectOne(lead.id, e.target.checked)}
                        color='primary'
                      />
                    </TableCell>
                    <TableCell className={styles.tableBodyCell} component='th' scope='row'>
                      {lead.name || '-'}
                    </TableCell>
                    <TableCell className={`${styles.tableBodyCell} ${styles.tableCellMobile}`}>
                      {lead.phone || '-'}
                    </TableCell>
                    <TableCell className={styles.tableBodyCell} align='center'>
                      <Typography variant='caption'>{formatDate(lead.updatedAt)}</Typography>
                    </TableCell>
                    <TableCell
                      className={styles.tableBodyCell}
                      align='center'
                      onClick={(e) => e.stopPropagation()}
                    >
                      <FormControl size='small' className={styles.statusSelect}>
                        <Select
                          value={normalizeLeadStatus(lead.status)}
                          onChange={(e) =>
                            handleStatusChange(lead, e.target.value as LeadStatus)
                          }
                          className={clsx(
                            styles.statusSelectField,
                            styles[getLeadStatusClassName(lead.status)],
                          )}
                          renderValue={(value) => getLeadStatusLabel(value)}
                        >
                          {LEAD_STATUSES.map((status) => (
                            <MenuItem key={status} value={status}>
                              {getLeadStatusLabel(status)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell
                      className={styles.tableBodyCell}
                      align='center'
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Box style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <Button
                          variant='outlined'
                          color='info'
                          onClick={() => handleViewLead(lead)}
                          size='small'
                        >
                          Просмотр
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  return (
    <Box className={styles.container}>
      <DashboardHeaderBackLink link={ROUTE_PATHS.ADMIN_LAST} title={'Заявки с лендингов'} />

      <Box className={styles.tabsContainer}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label='lead applications tabs'
          className={styles.tabs}
        >
          <Tab label={`Подбор (${tabCounts.selection})`} {...a11yProps(0)} />
          <Tab label={`Страхование (${tabCounts.insurance})`} {...a11yProps(1)} />
          <Tab label={`Лизинг (${tabCounts.leasing})`} {...a11yProps(2)} />
        </Tabs>
      </Box>

      <Box sx={{ p: 3 }}>{renderTable(activeType)}</Box>
    </Box>
  );
};
